import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import styles from "./TestSearchSelect.module.scss";

/**
 * Searchable dropdown for selecting a lab test.
 * Allows the lab user to type either the test name or the service code
 * to filter the list. The component is presentational - it does not
 * fetch the services itself, so it can be reused in any place that
 * already has the catalog loaded.
 *
 * The options list is rendered through a React portal with position:
 * fixed coordinates so it escapes any parent container that uses
 * overflow: hidden / auto (e.g. tables with horizontal scroll).
 *
 * Props:
 * - services:        array of lab services (each with id, testName, serviceCode, ...)
 * - value:           currently-selected service id (number | string | "")
 * - onSelect:        (serviceId: string) => void
 * - loading:         boolean to render a loading state
 * - disabled:        boolean
 * - placeholder:     placeholder text
 * - emptyText:       text rendered when no result matches the query
 */
const TestSearchSelect = ({
  services = [],
  value = "",
  onSelect,
  loading = false,
  disabled = false,
  placeholder = "Search by test name or service code",
  emptyText = "No tests match your search",
}) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const selectedService = useMemo(
    () => services.find((s) => String(s.id) === String(value) && value !== ""),
    [services, value],
  );

  const selectedLabel = useMemo(() => {
    if (!selectedService) return "";
    return `${selectedService.testName}${
      selectedService.serviceCode ? ` (${selectedService.serviceCode})` : ""
    }`;
  }, [selectedService]);

  // Sync the input text with the selected service when value changes externally.
  useEffect(() => {
    setQuery(selectedLabel);
  }, [selectedLabel]);

  // Defensive: whenever the parent clears `value`, force-clear the visible
  // text and close the menu. This covers cancel buttons that reset state in
  // the same React batch as a mousedown that triggered our outside-click
  // handler.
  useEffect(() => {
    if (!value) {
      setQuery("");
      setOpen(false);
    }
  }, [value]);

  const updateMenuPosition = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setMenuPos({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  // Reposition the menu while it's open and the page scrolls/resizes.
  useEffect(() => {
    if (!open) return undefined;
    updateMenuPosition();
    const onScrollOrResize = () => updateMenuPosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open, updateMenuPosition]);

  // Close on outside click (account for the portal-rendered list).
  useEffect(() => {
    const handleClickOutside = (e) => {
      const inContainer =
        containerRef.current && containerRef.current.contains(e.target);
      const inList = listRef.current && listRef.current.contains(e.target);
      if (!inContainer && !inList) {
        setOpen(false);
        setQuery(selectedLabel);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [selectedLabel]);

  const filteredServices = useMemo(() => {
    if (!Array.isArray(services)) return [];
    const trimmed = query.trim().toLowerCase();
    const isShowingSelected = selectedLabel && query === selectedLabel;
    if (!trimmed || isShowingSelected) return services;
    return services.filter((service) => {
      const name = (service.testName || "").toLowerCase();
      const code = (service.serviceCode || "").toLowerCase();
      return name.includes(trimmed) || code.includes(trimmed);
    });
  }, [services, query, selectedLabel]);

  // Keep the highlight in range as the filtered list changes.
  useEffect(() => {
    if (highlightIndex >= filteredServices.length) {
      setHighlightIndex(0);
    }
  }, [filteredServices, highlightIndex]);

  const handleInputChange = (e) => {
    setQuery(e.target.value);
    if (!open) {
      setOpen(true);
      updateMenuPosition();
    }
    setHighlightIndex(0);
  };

  const handleFocus = () => {
    if (disabled) return;
    setOpen(true);
    updateMenuPosition();
    if (selectedService && query) {
      // Clear so the user can start typing freely.
      setQuery("");
    }
  };

  const handleSelect = (service) => {
    if (!service) return;
    onSelect && onSelect(String(service.id));
    setQuery(
      `${service.testName}${
        service.serviceCode ? ` (${service.serviceCode})` : ""
      }`,
    );
    setOpen(false);
    if (inputRef.current) inputRef.current.blur();
  };

  const handleKeyDown = (e) => {
    if (disabled) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        updateMenuPosition();
      }
      setHighlightIndex((i) => Math.min(i + 1, filteredServices.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (open && filteredServices[highlightIndex]) {
        handleSelect(filteredServices[highlightIndex]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  // Auto-scroll the highlighted option into view.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const item = listRef.current.querySelector(
      `[data-idx="${highlightIndex}"]`,
    );
    if (item && typeof item.scrollIntoView === "function") {
      item.scrollIntoView({ block: "nearest" });
    }
  }, [highlightIndex, open]);

  const handleClear = (e) => {
    e.stopPropagation();
    onSelect && onSelect("");
    setQuery("");
    setOpen(true);
    updateMenuPosition();
    if (inputRef.current) inputRef.current.focus();
  };

  const showMenu = open && !disabled && !loading;

  const menu = showMenu
    ? createPortal(
        <ul
          className={styles["options-list"]}
          ref={listRef}
          role="listbox"
          style={{
            position: "fixed",
            top: menuPos.top,
            left: menuPos.left,
            width: menuPos.width,
          }}
        >
          {filteredServices.length === 0 ? (
            <li className={styles["empty"]}>{emptyText}</li>
          ) : (
            filteredServices.map((service, idx) => (
              <li
                key={service.id}
                data-idx={idx}
                className={`${styles["option"]} ${
                  idx === highlightIndex ? styles["highlighted"] : ""
                } ${
                  String(service.id) === String(value) ? styles["selected"] : ""
                }`}
                onMouseDown={(e) => {
                  // mousedown so the input doesn't blur before we register.
                  e.preventDefault();
                  handleSelect(service);
                }}
                onMouseEnter={() => setHighlightIndex(idx)}
                role="option"
                aria-selected={String(service.id) === String(value)}
              >
                <span className={styles["option-name"]}>
                  {service.testName || "Unnamed test"}
                </span>
                {service.serviceCode && (
                  <span className={styles["option-code"]}>
                    {service.serviceCode}
                  </span>
                )}
              </li>
            ))
          )}
        </ul>,
        document.body,
      )
    : null;

  return (
    <div
      className={styles["search-select"]}
      ref={containerRef}
      data-disabled={disabled || undefined}
    >
      <div className={styles["input-wrapper"]}>
        <input
          ref={inputRef}
          type="text"
          className={styles["search-input"]}
          value={query}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={loading ? "Loading tests..." : placeholder}
          disabled={disabled || loading}
          autoComplete="off"
        />
        {value && !disabled && !loading && (
          <button
            type="button"
            className={styles["clear-btn"]}
            onClick={handleClear}
            aria-label="Clear selection"
          >
            ×
          </button>
        )}
        <span className={styles["chevron"]} aria-hidden="true">
          ▾
        </span>
      </div>

      {menu}
    </div>
  );
};

export default TestSearchSelect;
